import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CreditCard } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';
import api from '../utils/api';

const Checkout = () => {
  const navigate = useNavigate();
  const { cartItems, cartTotal, clearCart } = useCart();
  const [loading, setLoading] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('razorpay');
  
  const [deliveryInfo, setDeliveryInfo] = useState({
    name: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    pincode: ''
  });

  const handleInputChange = (e) => {
    setDeliveryInfo({ ...deliveryInfo, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (cartItems.length === 0) {
      toast.error('Your cart is empty');
      return;
    }

    setLoading(true);

    try {
      const orderItems = cartItems.map(item => ({
        product_id: item.product_id,
        name: item.product.name,
        price: item.product.price,
        quantity: item.quantity,
        custom_size: item.custom_size || ''
      }));

      // For now, creating order with pending payment
      const orderData = {
        items: orderItems,
        total: cartTotal,
        payment_method: paymentMethod,
        payment_status: 'pending',
        delivery_info: deliveryInfo
      };

      const response = await api.post('/orders', orderData);
      
      await clearCart();
      toast.success('Order placed successfully!');
      navigate(`/orders/${response.data.id}`);
    } catch (error) {
      console.error('Order error:', error);
      toast.error('Failed to place order');
    } finally {
      setLoading(false);
    }
  };

  if (cartItems.length === 0) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <p className="text-xl text-slate-600 mb-4">Your cart is empty</p>
            <Button onClick={() => navigate('/products')}>Continue Shopping</Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />

      <div className="flex-1 bg-slate-50 py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="font-heading text-4xl lg:text-5xl font-bold text-primary mb-8 uppercase tracking-tight" data-testid="checkout-heading">
            Checkout
          </h1>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Checkout Form */}
            <div className="lg:col-span-2">
              <form onSubmit={handleSubmit} className="bg-white rounded-sm shadow-sm p-6 space-y-6">
                <div>
                  <h2 className="font-heading text-2xl font-bold text-primary mb-4 uppercase">Delivery Information</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="name">Full Name *</Label>
                      <Input
                        id="name"
                        name="name"
                        value={deliveryInfo.name}
                        onChange={handleInputChange}
                        required
                        data-testid="delivery-name"
                      />
                    </div>
                    <div>
                      <Label htmlFor="phone">Phone Number *</Label>
                      <Input
                        id="phone"
                        name="phone"
                        value={deliveryInfo.phone}
                        onChange={handleInputChange}
                        required
                        data-testid="delivery-phone"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <Label htmlFor="address">Address *</Label>
                      <Input
                        id="address"
                        name="address"
                        value={deliveryInfo.address}
                        onChange={handleInputChange}
                        required
                        data-testid="delivery-address"
                      />
                    </div>
                    <div>
                      <Label htmlFor="city">City *</Label>
                      <Input
                        id="city"
                        name="city"
                        value={deliveryInfo.city}
                        onChange={handleInputChange}
                        required
                        data-testid="delivery-city"
                      />
                    </div>
                    <div>
                      <Label htmlFor="state">State *</Label>
                      <Input
                        id="state"
                        name="state"
                        value={deliveryInfo.state}
                        onChange={handleInputChange}
                        required
                        data-testid="delivery-state"
                      />
                    </div>
                    <div>
                      <Label htmlFor="pincode">Pincode *</Label>
                      <Input
                        id="pincode"
                        name="pincode"
                        value={deliveryInfo.pincode}
                        onChange={handleInputChange}
                        required
                        data-testid="delivery-pincode"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <h2 className="font-heading text-2xl font-bold text-primary mb-4 uppercase">Payment Method</h2>
                  <div className="space-y-3">
                    <label className="flex items-center p-4 border-2 border-slate-200 rounded-sm cursor-pointer hover:border-secondary" data-testid="payment-razorpay">
                      <input
                        type="radio"
                        name="payment"
                        value="razorpay"
                        checked={paymentMethod === 'razorpay'}
                        onChange={(e) => setPaymentMethod(e.target.value)}
                        className="mr-3"
                      />
                      <CreditCard className="h-5 w-5 mr-2" />
                      <span className="font-medium">Razorpay (Cards, UPI, Wallets)</span>
                    </label>
                    <label className="flex items-center p-4 border-2 border-slate-200 rounded-sm cursor-pointer hover:border-secondary" data-testid="payment-paypal">
                      <input
                        type="radio"
                        name="payment"
                        value="paypal"
                        checked={paymentMethod === 'paypal'}
                        onChange={(e) => setPaymentMethod(e.target.value)}
                        className="mr-3"
                      />
                      <CreditCard className="h-5 w-5 mr-2" />
                      <span className="font-medium">PayPal</span>
                    </label>
                    <label className="flex items-center p-4 border-2 border-slate-200 rounded-sm cursor-pointer hover:border-secondary" data-testid="payment-cod">
                      <input
                        type="radio"
                        name="payment"
                        value="cod"
                        checked={paymentMethod === 'cod'}
                        onChange={(e) => setPaymentMethod(e.target.value)}
                        className="mr-3"
                      />
                      <span className="font-medium">Cash on Delivery</span>
                    </label>
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full"
                  disabled={loading}
                  data-testid="place-order-button"
                >
                  {loading ? 'Placing Order...' : 'Place Order'}
                </Button>
              </form>
            </div>

            {/* Order Summary */}
            <div>
              <div className="bg-white rounded-sm shadow-sm p-6 sticky top-24" data-testid="checkout-summary">
                <h2 className="font-heading text-2xl font-bold text-primary mb-4 uppercase">Order Summary</h2>
                <div className="space-y-3 mb-6">
                  {cartItems.map((item) => (
                    <div key={item.product_id} className="flex justify-between text-sm">
                      <span className="text-slate-600">
                        {item.product?.name} x{item.quantity}
                        {item.custom_size && <span className="text-secondary ml-1">({item.custom_size})</span>}
                      </span>
                      <span className="font-medium">₹{(item.product?.price * item.quantity).toFixed(2)}</span>
                    </div>
                  ))}
                  <div className="border-t border-slate-200 pt-3">
                    <div className="flex justify-between text-xl font-bold text-primary">
                      <span>Total</span>
                      <span data-testid="checkout-total">₹{cartTotal.toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Checkout;